import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { SERVICES } from "../data/siteData";
import glolexLogo from "../assests/images/glolex_logo.jpeg";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobSvcOpen, setMobSvcOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close everything on route change
  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobSvcOpen(false);
  }, [location]);

  // Close desktop dropdown on outside click
  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setServicesOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isServices = location.pathname.startsWith("/services");

  const col1 = SERVICES.slice(0, 5);
  const col2 = SERVICES.slice(5, 10);
  const col3 = SERVICES.slice(10, 15);

  // ── shared link style helpers ────────────────────────────────────────────
  const desktopLinkBase = {
    padding: "8px 13px",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "Inter, sans-serif",
    letterSpacing: "0.01em",
    textDecoration: "none",
    borderRadius: "6px",
    transition: "color .2s, background .2s",
    color: "#1B2B4B",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  };

  return (
    <header
      id="navbar"
      style={{
        position: "sticky",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: "#fff",
        borderBottom: "1px solid #E5E7EB",
        boxShadow: scrolled
          ? "0 4px 20px rgba(0,0,0,0.08)"
          : "0 2px 10px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.25s ease",
      }}
    >
      <div className="container-xl px-4">
        <nav
          className="navbar navbar-expand-lg"
          style={{ padding: "14px 0", minHeight: "80px" }}
        >

          {/* ── Logo ──────────────────────────────────────────────────────── */}
          <Link
            to="/"
            className="navbar-brand d-flex align-items-center"
            aria-label="GLOLEX Business Solutions Home"
            style={{ textDecoration: "none", marginRight: "24px", padding: 0 }}
          >
            <img
              src={glolexLogo}
              alt="Glolex logo"
              style={{ width: "auto", maxWidth: "240px", maxHeight: "56px", objectFit: "contain", display: "block", transform: "scale(1.20)", transformOrigin: "left center" }}
            />
          </Link>

          {/* ── Hamburger ─────────────────────────────────────────────────── */}
          <button
            id="mobile-menu-toggle"
            className="navbar-toggler border-0"
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation"
            aria-expanded={mobileOpen}
            style={{ boxShadow: "none", padding: "6px" }}
          >
            <span style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              {[0, 1, 2].map((i) => (
                <span key={i} style={{
                  display: "block", width: "22px", height: "2px",
                  background: "#1B2B4B",
                  borderRadius: "2px",
                  transition: "all .25s",
                  transform: i === 0 && mobileOpen ? "rotate(45deg) translate(5px,5px)"
                    : i === 2 && mobileOpen ? "rotate(-45deg) translate(5px,-5px)"
                      : "none",
                  opacity: i === 1 && mobileOpen ? 0 : 1,
                }} />
              ))}
            </span>
          </button>

          {/* ── Desktop Nav ───────────────────────────────────────────────── */}
          <div className="d-none d-lg-flex align-items-center flex-grow-1">
            <ul className="navbar-nav align-items-center gap-1 me-auto ms-2">

              <li className="nav-item">
                <NavLink to="/" end
                  style={({ isActive }) => ({ ...desktopLinkBase, color: isActive ? "#2BAABC" : "#1B2B4B" })}
                  onMouseEnter={e => { if (!e.currentTarget.style.color.includes("2BAABC")) e.currentTarget.style.color = "#2BAABC"; }}
                  onMouseLeave={e => { if (!location.pathname.endsWith("/") || location.pathname !== "/") e.currentTarget.style.color = "#1B2B4B"; }}
                >
                  Home
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink to="/about"
                  style={({ isActive }) => ({ ...desktopLinkBase, color: isActive ? "#2BAABC" : "#1B2B4B" })}
                >
                  About Us
                </NavLink>
              </li>

              {/* Services Mega Dropdown */}
              <li className="nav-item position-relative" ref={dropdownRef}>
                {/* Label → navigates to /services | Chevron → toggles dropdown */}
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <NavLink
                    to="/services"
                    id="services-nav-link"
                    style={({ isActive }) => ({
                      ...desktopLinkBase,
                      color: isActive ? "#2BAABC" : "#1B2B4B",
                      paddingRight: "4px",
                    })}
                    onMouseEnter={e => { e.currentTarget.style.color = "#2BAABC"; }}
                    onMouseLeave={e => { if (!isServices) e.currentTarget.style.color = "#1B2B4B"; }}
                  >
                    Services
                  </NavLink>
                  <button
                    id="services-dropdown-btn"
                    aria-label="Browse service categories"
                    aria-expanded={servicesOpen}
                    onClick={() => setServicesOpen(!servicesOpen)}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      padding: "8px 6px", display: "flex", alignItems: "center",
                      color: isServices ? "#2BAABC" : "#6B7280",
                      transition: "color .2s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = "#2BAABC"; }}
                    onMouseLeave={e => { if (!isServices) e.currentTarget.style.color = "#6B7280"; }}
                  >
                    <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      style={{ transform: servicesOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </span>

                {/* Mega Menu */}
                {servicesOpen && (
                  <div
                    id="services-mega-menu"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 10px)",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "800px",
                      background: "#fff",
                      borderRadius: "10px",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.10)",
                      border: "1px solid #E5E7EB",
                      padding: "20px",
                      zIndex: 1001,
                    }}
                  >
                    {/* Header strip */}
                    <div style={{ borderBottom: "1px solid #F3F4F6", marginBottom: "14px", paddingBottom: "10px" }}>
                      <span style={{ fontSize: "10px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.14em" }}>
                        Our 15 Service Areas
                      </span>
                    </div>

                    <div className="row g-0">
                      {[col1, col2, col3].map((col, ci) => (
                        <div key={ci} className="col-4">
                          {col.map((svc) => (
                            <Link
                              key={svc.id}
                              to={`/services/${svc.slug}`}
                              onClick={() => setServicesOpen(false)}
                              style={{
                                display: "flex", alignItems: "center", gap: "10px",
                                padding: "8px 10px", borderRadius: "7px",
                                textDecoration: "none", marginBottom: "2px",
                                transition: "background .15s, color .15s",
                                color: "#374151",
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.background = "#F0FAFB";
                                e.currentTarget.querySelector(".svc-title").style.color = "#2BAABC";
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.querySelector(".svc-title").style.color = "#374151";
                              }}
                            >
                              <span style={{ fontSize: "16px", lineHeight: 1, flexShrink: 0 }}>{svc.icon}</span>
                              <span className="svc-title" style={{ fontSize: "12.5px", fontWeight: 500, color: "#374151", lineHeight: 1.35, transition: "color .15s" }}>
                                {svc.title}
                              </span>
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>

                    <div style={{ borderTop: "1px solid #F3F4F6", marginTop: "14px", paddingTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", color: "#9CA3AF" }}>Full spectrum of Customs, Trade Compliance & Regulatory Licensing</span>
                      <Link
                        to="/services"
                        onClick={() => setServicesOpen(false)}
                        style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#2BAABC", fontWeight: 600, fontSize: "12.5px", textDecoration: "none" }}
                      >
                        View All Services
                        <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                )}
              </li>

              <li className="nav-item">
                <NavLink to="/industries"
                  style={({ isActive }) => ({ ...desktopLinkBase, color: isActive ? "#2BAABC" : "#1B2B4B" })}
                >
                  Industries
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/insights"
                  style={({ isActive }) => ({ ...desktopLinkBase, color: isActive ? "#2BAABC" : "#1B2B4B" })}
                >
                  Insights
                </NavLink>
              </li>
              {/* TEMPORARILY HIDDEN – Case Studies
              <li className="nav-item">
                <NavLink to="/case-studies"
                  style={({ isActive }) => ({ ...desktopLinkBase, color: isActive ? "#2BAABC" : "#1B2B4B" })}
                >
                  Case Studies
                </NavLink>
              </li>
              */}
              <li className="nav-item">
                <NavLink to="/contact"
                  style={({ isActive }) => ({ ...desktopLinkBase, color: isActive ? "#2BAABC" : "#1B2B4B" })}
                >
                  Contact Us
                </NavLink>
              </li>

            </ul>

            {/* ── Desktop CTA ─────────────────────────────────────────────── */}
            <Link
              to="/contact"
              id="navbar-cta"
              style={{
                background: "#2BAABC",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "13.5px",
                fontFamily: "Inter, sans-serif",
                textDecoration: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                transition: "background .2s, transform .2s",
                letterSpacing: "0.01em",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#1d8fa0"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#2BAABC"; e.currentTarget.style.transform = "none"; }}
            >
              Request a Consultation
            </Link>
          </div>
        </nav>
      </div>

      {/* ── Mobile Menu ──────────────────────────────────────────────────────── */}
      <div
        id="mobile-menu"
        style={{
          background: "#fff",
          borderTop: "1px solid #E5E7EB",
          maxHeight: mobileOpen ? "100vh" : "0",
          overflow: "hidden",
          transition: "max-height .35s ease",
        }}
      >
        <div style={{ padding: "12px 16px 20px" }}>

          {/* Regular nav links */}
          {[
            { label: "Home", path: "/" },
            { label: "About Us", path: "/about" },
            { label: "Industries", path: "/industries" },
            { label: "Insights", path: "/insights" },
            /* TEMPORARILY HIDDEN: { label: "Case Studies", path: "/case-studies" }, */
            { label: "Contact Us", path: "/contact" },
          ].map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              style={({ isActive }) => ({
                display: "block",
                padding: "12px 14px",
                borderRadius: "8px",
                marginBottom: "2px",
                textDecoration: "none",
                fontSize: "15px",
                fontWeight: 500,
                fontFamily: "Inter, sans-serif",
                color: isActive ? "#2BAABC" : "#1B2B4B",
                background: isActive ? "rgba(43,170,188,0.06)" : "transparent",
              })}
            >
              {item.label}
            </NavLink>
          ))}

          {/* Mobile Services Accordion */}
          <div>
            {/* Label → navigates to /services | Chevron → toggles accordion */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "2px 0", marginBottom: "2px",
              background: isServices ? "rgba(43,170,188,0.06)" : "transparent",
              borderRadius: "8px",
            }}>
              <NavLink
                to="/services"
                id="mobile-services-nav-link"
                style={({ isActive }) => ({
                  flex: 1,
                  padding: "10px 14px",
                  textDecoration: "none",
                  fontSize: "15px", fontWeight: 500,
                  fontFamily: "Inter, sans-serif",
                  color: isActive ? "#2BAABC" : "#1B2B4B",
                })}
              >
                Services
              </NavLink>
              <button
                aria-label="Browse service categories"
                aria-expanded={mobSvcOpen}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: "10px 14px", display: "flex", alignItems: "center",
                  color: isServices ? "#2BAABC" : "#6B7280",
                  flexShrink: 0,
                }}
                onClick={() => setMobSvcOpen(!mobSvcOpen)}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  style={{ transform: mobSvcOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {mobSvcOpen && (
              <div style={{ paddingLeft: "8px", paddingBottom: "4px" }}>
                {SERVICES.map((svc) => (
                  <Link
                    key={svc.id}
                    to={`/services/${svc.slug}`}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px",
                      padding: "9px 14px", borderRadius: "7px",
                      textDecoration: "none",
                      color: location.pathname === `/services/${svc.slug}` ? "#2BAABC" : "#374151",
                      fontSize: "13.5px", fontWeight: 500,
                    }}
                  >
                    <span style={{ fontSize: "15px" }}>{svc.icon}</span>
                    <span>{svc.title}</span>
                  </Link>
                ))}
                <Link
                  to="/services"
                  style={{ display: "block", padding: "9px 14px", color: "#2BAABC", fontSize: "13px", fontWeight: 600, textDecoration: "none" }}
                >
                  View All Services →
                </Link>
              </div>
            )}
          </div>

          {/* Mobile CTA */}
          <div style={{ marginTop: "14px", paddingTop: "14px", borderTop: "1px solid #E5E7EB" }}>
            <Link
              to="/contact"
              style={{
                display: "block", textAlign: "center",
                background: "#2BAABC", color: "#fff",
                padding: "14px", borderRadius: "8px",
                fontWeight: 600, fontSize: "15px",
                fontFamily: "Inter, sans-serif",
                textDecoration: "none",
              }}
            >
              Request a Consultation
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
